/**
 * Recorte de imagem por 4 pontos (cantos de um documento fotografado torto): a região delimitada pelos
 * pontos é "endireitada" num retângulo, como um scanner de documentos. Implementado com uma transformação
 * projetiva (homografia) num canvas — sem biblioteca externa.
 *
 * Pontos normalizados (0–1, relativos à imagem já com a orientação EXIF aplicada), na ordem:
 * superior esquerdo, superior direito, inferior direito, inferior esquerdo.
 */

export const PONTOS_PADRAO = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
];

const LADO_MAXIMO_PX = 2400; // mesmo limite de imagemParaPdf — fotos de celular têm 4000px+

export function pontosSaoPadrao(pontos) {
    return pontos.every((p, i) => Math.abs(p.x - PONTOS_PADRAO[i].x) < 0.001 && Math.abs(p.y - PONTOS_PADRAO[i].y) < 0.001);
}

/** Resolve o sistema linear A·x = b (Gauss com pivoteamento parcial). */
function resolverSistema(a, b) {
    const n = b.length;
    const m = a.map((linha, i) => [...linha, b[i]]);
    for (let col = 0; col < n; col++) {
        let pivo = col;
        for (let l = col + 1; l < n; l++) if (Math.abs(m[l][col]) > Math.abs(m[pivo][col])) pivo = l;
        if (Math.abs(m[pivo][col]) < 1e-12) throw new Error("Pontos do recorte inválidos (alinhados ou repetidos).");
        [m[col], m[pivo]] = [m[pivo], m[col]];
        for (let l = 0; l < n; l++) {
            if (l === col) continue;
            const fator = m[l][col] / m[col][col];
            for (let c = col; c <= n; c++) m[l][c] -= fator * m[col][c];
        }
    }
    return m.map((linha, i) => linha[n] / linha[i]);
}

/**
 * Homografia que leva cada ponto de `origens` ao ponto correspondente de `destinos` (4 pares).
 * Devolve [a, b, c, d, e, f, g, h] de: x' = (a·x + b·y + c) / (g·x + h·y + 1), y' = (d·x + e·y + f) / (g·x + h·y + 1).
 */
export function calcularHomografia(origens, destinos) {
    const a = [];
    const b = [];
    for (let i = 0; i < 4; i++) {
        const { x, y } = origens[i];
        const { x: X, y: Y } = destinos[i];
        a.push([x, y, 1, 0, 0, 0, -X * x, -X * y]);
        b.push(X);
        a.push([0, 0, 0, x, y, 1, -Y * x, -Y * y]);
        b.push(Y);
    }
    return resolverSistema(a, b);
}

export function aplicarHomografia(h, x, y) {
    const w = h[6] * x + h[7] * y + 1;
    return { x: (h[0] * x + h[1] * y + h[2]) / w, y: (h[3] * x + h[4] * y + h[5]) / w };
}

const distancia = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);

/** Recorta e endireita a região dos 4 pontos. Devolve um Blob (PNG se a origem for PNG, senão JPEG). */
export async function recortarPerspectiva(blobImagem, pontos) {
    const bitmap = await createImageBitmap(blobImagem, { imageOrientation: "from-image" });
    const escala = Math.min(1, LADO_MAXIMO_PX / Math.max(bitmap.width, bitmap.height));
    const largOrigem = Math.round(bitmap.width * escala);
    const altOrigem = Math.round(bitmap.height * escala);
    const canvasOrigem = document.createElement("canvas");
    canvasOrigem.width = largOrigem;
    canvasOrigem.height = altOrigem;
    const ctxOrigem = canvasOrigem.getContext("2d");
    ctxOrigem.drawImage(bitmap, 0, 0, largOrigem, altOrigem);
    bitmap.close?.();
    const origem = ctxOrigem.getImageData(0, 0, largOrigem, altOrigem).data;

    // Cantos em pixels da imagem de origem.
    const cantos = pontos.map(p => ({ x: p.x * (largOrigem - 1), y: p.y * (altOrigem - 1) }));
    const largura = Math.max(1, Math.round(Math.max(distancia(cantos[0], cantos[1]), distancia(cantos[3], cantos[2]))));
    const altura = Math.max(1, Math.round(Math.max(distancia(cantos[0], cantos[3]), distancia(cantos[1], cantos[2]))));

    // Mapeia cada pixel do retângulo de destino de volta para a origem (mapeamento inverso, sem buracos).
    const destinoParaOrigem = calcularHomografia(
        [{ x: 0, y: 0 }, { x: largura - 1, y: 0 }, { x: largura - 1, y: altura - 1 }, { x: 0, y: altura - 1 }],
        cantos,
    );
    const canvasDestino = document.createElement("canvas");
    canvasDestino.width = largura;
    canvasDestino.height = altura;
    const ctxDestino = canvasDestino.getContext("2d");
    const imagemDestino = ctxDestino.createImageData(largura, altura);
    const destino = imagemDestino.data;

    // Laço quente (milhões de pixels): conta inline, sem criar objetos por pixel.
    const [a, b, c, d, e, f, g, h] = destinoParaOrigem;
    const maxX = largOrigem - 1;
    const maxY = altOrigem - 1;
    let i = 0;
    for (let v = 0; v < altura; v++) {
        for (let u = 0; u < largura; u++, i += 4) {
            const w = g * u + h * v + 1;
            const x = (a * u + b * v + c) / w;
            const y = (d * u + e * v + f) / w;
            // Interpolação bilinear (bordas fixadas dentro da imagem).
            const x0 = x <= 0 ? 0 : x >= maxX ? maxX : x | 0;
            const y0 = y <= 0 ? 0 : y >= maxY ? maxY : y | 0;
            const x1 = x0 < maxX ? x0 + 1 : maxX;
            const y1 = y0 < maxY ? y0 + 1 : maxY;
            const fx = x <= x0 ? 0 : x >= x0 + 1 ? 1 : x - x0;
            const fy = y <= y0 ? 0 : y >= y0 + 1 ? 1 : y - y0;
            const i00 = (y0 * largOrigem + x0) * 4;
            const i10 = (y0 * largOrigem + x1) * 4;
            const i01 = (y1 * largOrigem + x0) * 4;
            const i11 = (y1 * largOrigem + x1) * 4;
            const w00 = (1 - fx) * (1 - fy);
            const w10 = fx * (1 - fy);
            const w01 = (1 - fx) * fy;
            const w11 = fx * fy;
            destino[i] = origem[i00] * w00 + origem[i10] * w10 + origem[i01] * w01 + origem[i11] * w11;
            destino[i + 1] = origem[i00 + 1] * w00 + origem[i10 + 1] * w10 + origem[i01 + 1] * w01 + origem[i11 + 1] * w11;
            destino[i + 2] = origem[i00 + 2] * w00 + origem[i10 + 2] * w10 + origem[i01 + 2] * w01 + origem[i11 + 2] * w11;
            destino[i + 3] = origem[i00 + 3] * w00 + origem[i10 + 3] * w10 + origem[i01 + 3] * w01 + origem[i11 + 3] * w11;
        }
    }
    ctxDestino.putImageData(imagemDestino, 0, 0);

    const ehPng = blobImagem.type === "image/png";
    return new Promise((resolve, reject) => canvasDestino.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error("Não foi possível gerar a imagem recortada."))),
        ehPng ? "image/png" : "image/jpeg",
        0.92,
    ));
}
